import React from 'react';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import {
    AllParamNames,
    useAccountTabNavigation,
    useActivityTabNavigation,
    useNotesTabNavigation,
    useProfilesTabNavigation,
} from '../../../utils/appNavigationNames';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { getIntl } from '../../../utils/intl';
import Text from '../Text';
import Stack from '../Stack';
import { getSpacing } from '../../../utils/spacing';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import { MedicalDataTypes } from '../../../primary-record-types';
import { auditLogDescriptors, pluralResourceCategoryMessages, reportTypeMessages } from '../../../utils/sharedMessages';
import BaseHeader from './BaseHeader';
import { useAuditLog } from '../../../hooks/api/useAuditLog';
import _truncate from 'lodash/truncate';
import useNotes from '../../../hooks/api/useNotes';
import useCareTeamMember from '../../../hooks/api/useCareTeamMember';
import { isProvider, isSupport } from '../../../utils/careTeamUtils';
import getName from '../../../utils/getName';
import { useCollaborator } from '../../../hooks/api/useCollaborator';
import useProfileConnectedHealthSystems from '../../../hooks/api/useProfileConnectedHealthSystems';
import useFastenConnections from '../../../hooks/api/useFastenConnections';
import { useReports } from '../../../hooks/api/useReports';
import useMedicalProfileFiles from '../../../hooks/api/useMedicalProfileFiles';
import { useDateLabs } from '../../../hooks/api/useDateLabs';
import getDateString from '../../../utils/getDateString';
import { useLabHistory } from '../../../hooks/api/useLabHistory';
import useResource from '../../../hooks/api/useResource';
import { useResourceTitle } from '../../../utils/resourceUtils/getResourceTitle';
import { useCollaboratorInvitation } from '../../../hooks/api/useCollaboratorInvitation';
import useProfilePendingTransferRequest from '../../../hooks/api/useProfilePendingTransferRequest';
import { getCollaboratorName } from '../../../utils/collaboratorUtils';
import { useIsProApp } from '../../../hooks/useAppType';
import { useOrganizationClientRecords } from '../../../hooks/api/useOrganizationClientRecords';
import OrganizationButton from './OrganizationButton';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';

const maybeTruncate = (text: string) => {
    return _truncate(text, { length: 34 });
};

const intl = getIntl('DesktopHeader', {
    client: 'Client',
    headerImageAlt: 'Header Primary Record Logo',
    activity: 'Activity',
    notes: 'My Notes',
    myAccount: 'My Account',
    'profiles-root': 'Profiles',
    profile: 'Profile',
    'notes-root': 'Notes',
    'account-root': 'My Account',
    'activity-root': 'Activity',
    resourceCategory: 'Resource Category',
    'profile-notes': 'Notes',
    'profile-notes-without-note-id': 'Notes',
    'user-notes': 'User Notes',
    'activity-feed-details': 'Activity Details',
    'resource-category': 'Resource Category',
    'audit-log': 'Activity Details',
    'user-note': 'User Note',
    'add-note': 'Add Note',
    'edit-note': 'Edit Note',
    'transfer-note': 'Move Note',
    'account-information': 'Account Information',
    support: 'Support',
    about: 'About',
    'subscription-plans': 'Subscription',
    'privacy-policy': 'Privacy Policy',
    'account-deletion-confirm': 'Account Deletion Confirmation',
    'add-profile': 'Add Profile',
    'edit-profile': 'Profile Information',
    invitation: 'Invitation',
    'contact-details': 'Contact Details',
    'emergency-contacts': 'Emergency Contacts',
    'edit-emergency-contact': 'Edit',
    'add-emergency-contact': 'Add',
    'care-team': 'Care Team',
    'edit-care-team': 'Edit',
    'add-care-team': 'Add',
    'care-team-member': 'Care Team Member',
    'edit-care-team-member': 'Edit',
    'copy-care-team-member': 'Copy',
    'add-related-care-team-member': 'Add Related Care Team Member',
    'invite-collaborator': 'Invite Collaborator',
    'invite-details': 'Invite Details',
    collaborator: 'Collaborator',
    'add-transfer-request': 'Transfer Profile Ownership',
    'transfer-request': 'Transfer Request',
    'transfer-request-response': 'Respond',
    'add-card': 'Add Card',
    'edit-card': 'Edit Card',
    data: 'Data',
    'data-bulk-update': 'Bulk Update',
    'data-details': 'Data Details',
    'add-data': 'Add',
    'edit-data': 'Edit',
    'add-portal': 'Add',
    'add-fasten-portal': 'Add',
    'add-tefca-connection': 'Add',
    'add-profile-note': 'Add',
    'edit-profile-note': 'Edit',
    'copy-profile-note': 'Copy',
    'profile-note': 'Note',
    'transfer-profile-note': 'Transfer',
    'portal-connection': 'Portal Connection',
    'fasten-portal-connection': 'Portal Connection',
    reports: 'Reports',
    'add-shared-report': 'Add',
    'shared-report': 'Shared Report',
    files: 'Files',
    file: 'File',
    chat: 'Chat Assistant',
    'date-labs': 'Date Labs',
    'lab-history': 'Lab History',
    'emergency-contact': 'Emergency Contact',
    collaborators: 'Collaborators',
    'transfer-requests': 'Transfer Requests',
    cards: 'Cards',
    portals: 'Portals',
    'profile-from-note-id': 'Profile',
    'profile-from-note-destination': 'Profile',
    'profile-from-invitation': 'Profile',
    labsOn: 'Labs on {date}',
    'profile-from-transfer-request': 'Profile',
    'add-message': 'Add Message',
    'profile-activity': 'Activity',
    'message-profile': 'Send Message',
    'client-records': 'Client Records',
    'client-record': 'Client Record',
    'client-record-notes': 'Client Notes',
    'add-client-record-note': 'Add',
    'edit-client-record-note': 'Edit',
    'client-record-note': 'Note',
    preferences: 'Preferences',
    'client-activity-history': 'Activity History',
    access: 'Access',
    connections: 'Connections',
    'static-profile-notes': 'Notes',
    'bulk-add-client-records': 'Bulk Add',
    'add-suggested-folders': 'Add Suggested Folders',
    'edit-client-invitation': 'Edit Invitation',
    'bulk-add-client-profiles-groups': 'Bulk Add Groups',
    invoice: 'Invoice',
    'code-status': 'Code Status',
    'legal-decision-maker': 'Legal Decision Maker',
    messages: 'Messages',
    'send-note': 'Share',
    'visit-invite-emails': 'Subscribe to Visit Invites',
});

const proAppIntl = getIntl('DesktopHeader-pro-app', {
    'profiles-root': 'Clients',
    profile: 'Client',
});

type OneOfParams = AllParamNames[] | AllParamNames;

function useAssertParams(props: NativeStackHeaderProps, ...params: OneOfParams[]): string[] {
    const returnValue: string[] = [];
    for (const param of params) {
        const asArray = Array.isArray(param) ? param : [param];
        const targetParamName = asArray.find((_) => (props.route.params as any)?.[_] !== undefined);

        if (!targetParamName) {
            throw new Error(
                `Expected one of the following params: ${asArray.join(', ')}.  Received: ${JSON.stringify(
                    props.route.params,
                )}`,
            );
        }

        returnValue.push((props.route.params as any)[targetParamName]);
    }

    return returnValue;
}

const maybeRenderTrailingSlash = (renderTrailingSlash: boolean) =>
    renderTrailingSlash ? (
        <Text variant="header-breadcrumb-divider" style={{ marginLeft: getSpacing(2) }}>
            {'/'}
        </Text>
    ) : null;

const renderClickableCrumb = (text: string, onPress: () => void, renderTrailingSlash: boolean, index: number) => (
    <React.Fragment key={text + String(index)}>
        <PressableDynamicOpacity onPress={onPress} testID={`breadcrumb-${text}`}>
            <Text variant="header-breadcrumb-parent">{text}</Text>
        </PressableDynamicOpacity>
        {maybeRenderTrailingSlash(renderTrailingSlash)}
    </React.Fragment>
);

const renderTextCrumb = (text: string, renderTrailingSlash: boolean, index: number) => (
    <React.Fragment key={text + String(index)}>
        <Text
            variant={renderTrailingSlash ? 'header-breadcrumb-parent-non-clickable' : 'header-breadcrumb-leaf'}
            testID={`breadcrumb-${text}`}
        >
            {text}
        </Text>
        {maybeRenderTrailingSlash(renderTrailingSlash)}
    </React.Fragment>
);

const getStaticTextCrumb =
    (pathPart: BreadCrumbPathPartType) =>
    ({ isLast, index }: CrumbBaseProps) =>
        renderTextCrumb(intl(pathPart), !isLast, index);

const renderMaybeClickableCrumb = (
    text: string,
    onPress: () => void,
    renderTrailingSlash: boolean,
    index: number,
    isLast: boolean,
) => {
    if (!isLast) {
        return renderClickableCrumb(text, onPress, renderTrailingSlash, index);
    } else {
        return renderTextCrumb(text, renderTrailingSlash, index);
    }
};

const renderLoadingCrumb = (index: number, renderTrailingSlash: boolean) => (
    <React.Fragment key={'loading' + String(index)}>
        <SkeletonLoadingContainer isLoading>
            <Text variant="header-breadcrumb-leaf">{'Loading...'}</Text>
        </SkeletonLoadingContainer>
        {maybeRenderTrailingSlash(renderTrailingSlash)}
    </React.Fragment>
);

type StaticTextPressableCrumbProps = { pathPartType: BreadCrumbPathPartType; onPress: () => void };

const StaticPressableTextCrumb: React.FC<CrumbBaseProps & StaticTextPressableCrumbProps> = ({
    isLast,
    pathPartType,
    onPress,
    index,
}) => {
    const isProApp = useIsProApp();
    let text = intl(pathPartType);
    if (pathPartType === 'profiles-root' && isProApp) {
        text = proAppIntl('profiles-root');
    }
    return renderClickableCrumb(text, onPress, !isLast, index);
};

const ProfilesRootCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const profilesNavigation = useProfilesTabNavigation();
    return (
        <StaticPressableTextCrumb
            pathPartType="profiles-root"
            onPress={() => profilesNavigation.navigate('root')}
            index={index}
            isLast={isLast}
        />
    );
};

const ClientRecordsCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const profilesNavigation = useProfilesTabNavigation();
    return (
        <StaticPressableTextCrumb
            pathPartType="client-records"
            onPress={() => profilesNavigation.navigate('root')}
            index={index}
            isLast={isLast}
        />
    );
};

const ClientRecordCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...headerProps }) => {
    const [clientRecordId] = useAssertParams(headerProps, 'clientRecordId');
    const { isLoadingClientRecord, clientRecord } = useOrganizationClientRecords({
        organizationClientRecordId: clientRecordId,
    });

    if (isLoadingClientRecord) return renderLoadingCrumb(index, !isLast);

    if (!clientRecord) return null;

    return renderTextCrumb(getName(clientRecord.clientDetails) || intl('client'), !isLast, index);
};

const BaseProfileCrumb: React.FC<CrumbBaseProps & { profileId: string }> = ({ isLast, index, profileId }) => {
    const isMobile = useIsMobileLayout();
    const profilesNavigation = useProfilesTabNavigation();
    const { isLoadingProfile, profile } = useMedicalProfile({
        medicalProfileId: profileId,
    });

    if (isLoadingProfile) return renderLoadingCrumb(index, !isLast);

    if (isLast || !isMobile) {
        return renderTextCrumb(
            profile?.contactDetails?.preferredFirstName || profile?.contactDetails?.firstName || '',
            !isLast,
            index,
        );
    } else {
        return renderClickableCrumb(
            profile?.contactDetails?.preferredFirstName || profile?.contactDetails?.firstName || '',
            () => profilesNavigation.navigate('profile', { profileId }),
            !isLast,
            index,
        );
    }
};

const ProfileCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = (props) => {
    const [profileId] = useAssertParams(props, 'profileId');
    return <BaseProfileCrumb profileId={profileId} {...props} />;
};

const ProfileFromNoteIdCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = (props) => {
    const [noteId] = useAssertParams(props, 'noteId');
    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (isLoadingNote) return renderLoadingCrumb(props.index, !props.isLast);

    if (!note?.medicalProfileId) return null;

    return <BaseProfileCrumb profileId={note.medicalProfileId} {...props} />;
};

const ProfileFromNoteDestinationCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = (props) => {
    // have to any this because the type doesn't handle resource note destination params
    const [profileId] = useAssertParams(props, 'medicalProfileId' as any);
    return <BaseProfileCrumb profileId={profileId} {...props} />;
};

const ProfileFromInvitationCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = (props) => {
    const [profileId, invitationId] = useAssertParams(props, 'profileId', 'invitationId');
    const { isLoadingInvitation, invitation } = useCollaboratorInvitation({
        medicalProfileId: profileId,
        invitationId,
    });

    if (isLoadingInvitation || !invitation) return renderLoadingCrumb(props.index, !props.isLast);

    const name = invitation?.medicalProfile?.preferredFirstName || invitation?.medicalProfile.firstName;
    if (!name) return null;

    return renderTextCrumb(name, !props.isLast, props.index);
};

const ProfileFromTransferRequestCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = (props) => {
    const [profileId, transferRequestId] = useAssertParams(props, 'transferProfileId', 'transferRequestId');
    const { isLoading, profilePendingTransferRequest } = useProfilePendingTransferRequest({
        medicalProfileId: profileId,
        transferRequestId,
    });
    if (isLoading || !profilePendingTransferRequest) return renderLoadingCrumb(props.index, !props.isLast);

    const name =
        profilePendingTransferRequest?.profile?.preferredFirstName || profilePendingTransferRequest?.profile.firstName;

    if (!name) return null;

    return renderTextCrumb(name, !props.isLast, props.index);
};

const ProfileNotesWithoutNoteIdCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...headerProps
}) => {
    const isMobile = useIsMobileLayout();
    const profileTabNavigation = useProfilesTabNavigation();
    // have to any this because the type doesn't handle resource note destination params
    const [medicalProfileId] = useAssertParams(headerProps, 'medicalProfileId' as any);

    return renderMaybeClickableCrumb(
        intl('profile-notes'),
        () => {
            if (isMobile) {
                profileTabNavigation.navigate('profile-notes', {
                    profileId: medicalProfileId,
                });
            } else {
                profileTabNavigation.navigate('profile-notes', {
                    profileId: medicalProfileId,
                });
            }
        },
        !isLast,
        index,
        isLast,
    );
};

const ProfileNotesCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...headerProps }) => {
    const isMobile = useIsMobileLayout();
    const profileTabNavigation = useProfilesTabNavigation();
    const [noteId] = useAssertParams(headerProps, 'noteId');
    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (isLoadingNote) return renderLoadingCrumb(index, !isLast);

    if (!note || !note.medicalProfileId) return null;

    return renderMaybeClickableCrumb(
        intl('profile-notes'),
        () => {
            if (isMobile) {
                profileTabNavigation.navigate('profile-notes', {
                    profileId: note.medicalProfileId!,
                });
            } else {
                profileTabNavigation.navigate('profile-notes', {
                    profileId: note.medicalProfileId!,
                });
            }
        },
        !isLast,
        index,
        isLast,
    );
};

const AccountRootCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const accountNavigation = useAccountTabNavigation();
    return (
        <StaticPressableTextCrumb
            pathPartType="account-root"
            onPress={() => accountNavigation.navigate('root')}
            index={index}
            isLast={isLast}
        />
    );
};

const ActivityRootCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const activityNavigation = useActivityTabNavigation();
    return (
        <StaticPressableTextCrumb
            pathPartType="activity-root"
            onPress={() => activityNavigation.navigate('root')}
            index={index}
            isLast={isLast}
        />
    );
};

const ResourceCategoryCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...headerProps
}) => {
    const profilesNavigation = useProfilesTabNavigation();
    const [profileId, category] = useAssertParams(headerProps, 'profileId', 'category');

    return renderMaybeClickableCrumb(
        pluralResourceCategoryMessages(category as MedicalDataTypes.PrimaryRecordResourceCategory),
        () => {
            profilesNavigation.navigate('data', {
                profileId,
                category: category as MedicalDataTypes.PrimaryRecordResourceCategory,
            });
        },
        !isLast,
        index,
        isLast,
    );
};

const UserNotesCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const notesNavigation = useNotesTabNavigation();
    return (
        <StaticPressableTextCrumb
            pathPartType="user-notes"
            onPress={() => notesNavigation.navigate('root')}
            index={index}
            isLast={isLast}
        />
    );
};

const AuditLogCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...headerProps }) => {
    const [profileId, auditLogId] = useAssertParams(headerProps, 'profileId', 'auditLogId');
    const { auditLog, isLoadingAuditLog } = useAuditLog({
        medicalProfileId: profileId,
        auditLogId: auditLogId,
    });

    if (isLoadingAuditLog) return renderLoadingCrumb(index, !isLast);

    if (!auditLog || auditLog.status === 'unauthorized') return null;

    const targetAuditLog = auditLog.status === 'partially-invalid' ? auditLog.newAuditLog : auditLog.auditLog;

    const message = auditLogDescriptors[targetAuditLog.type];
    return renderTextCrumb(message, !isLast, index);
};

const UserNoteCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...headerProps }) => {
    const [noteId] = useAssertParams(headerProps, 'noteId');

    const notesNavigation = useNotesTabNavigation();
    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (isLoadingNote) return renderLoadingCrumb(index, !isLast);

    if (!note) return null;

    return renderMaybeClickableCrumb(
        maybeTruncate(note.title),
        () => notesNavigation.navigate('details', { noteId }),
        !isLast,
        index,
        isLast,
    );
};

const AddNoteCrumb = getStaticTextCrumb('add-note');

const EditNoteCrumb = getStaticTextCrumb('edit-note');

const TransferNoteCrumb = getStaticTextCrumb('transfer-note');

const AccountInformationCrumb = getStaticTextCrumb('account-information');

const SupportCrumb = getStaticTextCrumb('support');

const AboutCrumb = getStaticTextCrumb('about');

const SubscriptionPlansCrumb: React.FC<CrumbBaseProps> = ({ isLast, index }) => {
    const accountNavigation = useAccountTabNavigation();
    return renderMaybeClickableCrumb(
        intl('subscription-plans'),
        () => accountNavigation.navigate('subscription'),
        !isLast,
        index,
        isLast,
    );
};

const PrivacyPolicyCrumb = getStaticTextCrumb('privacy-policy');

const AccountDeletionConfirmCrumb = getStaticTextCrumb('account-deletion-confirm');

const AddProfileCrumb = getStaticTextCrumb('add-profile');

const EditProfileCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ index, isLast, ...headerProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(headerProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('edit-profile'),
        () => profileTabNavigation.navigate('edit', { profileId, firstName: '' }),
        !isLast,
        index,
        isLast,
    );
};

const InvitationCrumb = getStaticTextCrumb('invitation');

const ContactDetailsCrumb = getStaticTextCrumb('contact-details');

const EmergencyContactsCrumb = getStaticTextCrumb('emergency-contacts');

const EditEmergencyContactCrumb = getStaticTextCrumb('edit-emergency-contact');

const AddEmergencyContactCrumb = getStaticTextCrumb('add-emergency-contact');

const CareTeamCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const [profileId] = useAssertParams(navigationProps, 'profileId');
    const accountNavigation = useProfilesTabNavigation();
    return renderMaybeClickableCrumb(
        intl('care-team'),
        () =>
            accountNavigation.navigate('care-team', {
                profileId: profileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const EditCareTeamCrumb = getStaticTextCrumb('edit-care-team');

const AddCareTeamCrumb = getStaticTextCrumb('add-care-team');

const CareTeamMemberCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, memberId] = useAssertParams(navigationProps, 'profileId', 'careTeamMemberId');
    const { isLoadingCareTeamMember, careTeamMember } = useCareTeamMember({
        medicalProfileId: profileId,
        careTeamMemberId: memberId,
    });

    if (isLoadingCareTeamMember) return renderLoadingCrumb(index, !isLast);

    if (!careTeamMember) return null;

    const getText = () => {
        if (isSupport(careTeamMember) || isProvider(careTeamMember)) {
            return getName(careTeamMember.contactDetails) || careTeamMember.contactDetails.company || '';
        } else {
            return careTeamMember.metadata.insuranceProviderName;
        }
    };

    return renderMaybeClickableCrumb(
        getText(),
        () =>
            profileTabNavigation.navigate('care-team', {
                profileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const EditCareTeamMemberCrumb = getStaticTextCrumb('edit-care-team-member');

const CopyCareTeamMemberCrumb = getStaticTextCrumb('copy-care-team-member');

const AddRelatedCareTeamMemberCrumb = getStaticTextCrumb('add-related-care-team-member');

const InviteCollaboratorCrumb = getStaticTextCrumb('invite-collaborator');

const InviteDetailsCrumb = getStaticTextCrumb('invite-details');

const CollaboratorCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, collaboratorId] = useAssertParams(navigationProps, 'profileId', 'collaboratorId');
    const { isLoading, collaborator } = useCollaborator({
        medicalProfileId: profileId,
        collaboratorId: collaboratorId,
    });

    if (isLoading) return renderLoadingCrumb(index, !isLast);

    if (!collaborator) return null;

    return renderMaybeClickableCrumb(
        getCollaboratorName(collaborator),
        () =>
            profileTabNavigation.navigate('collaborator', {
                profileId,
                collaboratorId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const AddTransferRequestCrumb = getStaticTextCrumb('add-transfer-request');

const TransferRequestCrumb = getStaticTextCrumb('transfer-request');

const TransferRequestResponseCrumb = getStaticTextCrumb('transfer-request-response');

const AddCardCrumb = getStaticTextCrumb('add-card');

const EditCardCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const [description] = useAssertParams(navigationProps, 'description');

    return renderTextCrumb(description, !isLast, index);
};

const DataCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, category] = useAssertParams(navigationProps, 'profileId', 'category');

    return renderMaybeClickableCrumb(
        pluralResourceCategoryMessages(category as MedicalDataTypes.PrimaryRecordResourceCategory),
        () =>
            profileTabNavigation.navigate('data', {
                profileId,
                category: category as MedicalDataTypes.PrimaryRecordResourceCategory,
            }),
        !isLast,
        index,
        isLast,
    );
};

const DataBulkUpdateCrumb = getStaticTextCrumb('data-bulk-update');

const DataDetailsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, resourceId] = useAssertParams(navigationProps, 'profileId', 'resourceId');
    const { resource, isLoadingResource } = useResource({
        medicalProfileId: profileId,
        resourceId,
        resourceCategory: null,
    });
    const name = useResourceTitle(resource, profileId);

    if (isLoadingResource) return renderLoadingCrumb(index, !isLast);

    if (!resource) return null;

    return renderMaybeClickableCrumb(
        maybeTruncate(name),
        () =>
            profileTabNavigation.navigate('data-details', {
                profileId,
                resourceId,
                category: resource.resource.type,
            }),
        !isLast,
        index,
        isLast,
    );
};

const AddDataCrumb = getStaticTextCrumb('add-data');

const EditDataCrumb = getStaticTextCrumb('edit-data');

const AddPortalCrumb = getStaticTextCrumb('add-portal');

const AddFastenPortalCrumb = getStaticTextCrumb('add-fasten-portal');

const AddTefcaConnectionCrumb = getStaticTextCrumb('add-tefca-connection');

const AddProfileNoteCrumb = getStaticTextCrumb('add-profile-note');

const CopyProfileNoteCrumb = getStaticTextCrumb('copy-profile-note');

const ProfileNoteCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [noteId, profileId] = useAssertParams(navigationProps, 'noteId', 'profileId');
    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (isLoadingNote) return renderLoadingCrumb(index, !isLast);

    if (!note) return null;

    return renderMaybeClickableCrumb(
        maybeTruncate(note.title),
        () =>
            profileTabNavigation.navigate('note', {
                noteId: note.noteId,
                noteEntityType: note.entityType,
                profileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const ClientRecordNoteCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [noteId, clientRecordId] = useAssertParams(navigationProps, 'noteId', 'clientRecordId');
    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (isLoadingNote) return renderLoadingCrumb(index, !isLast);

    if (!note) return null;

    return renderMaybeClickableCrumb(
        maybeTruncate(note.title),
        () =>
            profileTabNavigation.navigate('client-record-note', {
                noteId,
                clientRecordId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const TransferProfileNoteCrumb = getStaticTextCrumb('transfer-profile-note');

const PortalConnectionCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, healthSystemId] = useAssertParams(navigationProps, 'profileId', 'healthSystemId');
    const { isLoadingHealthSystemConnectionDetails, healthSystemConnectionDetails } = useProfileConnectedHealthSystems({
        medicalProfileId: profileId,
        healthSystemId,
    });

    if (isLoadingHealthSystemConnectionDetails) return renderLoadingCrumb(index, !isLast);

    if (!healthSystemConnectionDetails) return null;

    return renderMaybeClickableCrumb(
        maybeTruncate(healthSystemConnectionDetails.endpoint.name),
        () =>
            profileTabNavigation.navigate('portal-connection', {
                profileId,
                healthSystemId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const FastenPortalConnectionCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, orgConnectionId] = useAssertParams(navigationProps, 'profileId', 'orgConnectionId');
    const { isLoading, connections } = useFastenConnections({ medicalProfileId: profileId });

    const connection = connections.find((c) => c.orgConnectionId === orgConnectionId);

    if (isLoading) return renderLoadingCrumb(index, !isLast);

    if (!connection) return null;

    // Use institution name if available, otherwise format platform name (e.g., "cigna" -> "Cigna")
    const platformName = connection.platformType
        ? connection.platformType.charAt(0).toUpperCase() + connection.platformType.slice(1).toLowerCase()
        : 'Unknown';
    const displayName = connection.institutionName || platformName;

    return renderMaybeClickableCrumb(
        maybeTruncate(displayName),
        () =>
            profileTabNavigation.navigate('fasten-portal-connection', {
                profileId,
                orgConnectionId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const ReportsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('reports'),
        () =>
            profileTabNavigation.navigate('reports', {
                profileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const AddSharedReportCrumb = getStaticTextCrumb('add-shared-report');

const SharedReportCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, sharedReportId] = useAssertParams(navigationProps, 'profileId', 'sharedReportId');

    const { isLoadingSharedReport, sharedReport } = useReports({
        medicalProfileId: profileId,
        sharedReportId,
    });

    if (isLoadingSharedReport) return renderLoadingCrumb(index, !isLast);

    if (!sharedReport) return null;

    return renderMaybeClickableCrumb(
        reportTypeMessages(sharedReport.reportType),
        () =>
            profileTabNavigation.navigate('shared-report', {
                profileId,
                sharedReportId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const FilesCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('files'),
        () =>
            profileTabNavigation.navigate('files', {
                profileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const FileCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, fileId] = useAssertParams(navigationProps, 'profileId', 'fileId');
    const { isLoadingFile, file } = useMedicalProfileFiles({
        medicalProfileId: profileId,
        medicalProfileFileId: fileId,
    });

    if (isLoadingFile) return renderLoadingCrumb(index, !isLast);

    if (!file) return null;

    return renderMaybeClickableCrumb(
        file.fileName,
        () =>
            profileTabNavigation.navigate('file', {
                profileId,
                fileId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const ChatCrumb = getStaticTextCrumb('chat');

const DateLabsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, date] = useAssertParams(navigationProps, 'profileId', 'date');

    const { isLoadingDateLabs, dateLabs } = useDateLabs({
        medicalProfileId: profileId,
        date: date,
    });

    if (isLoadingDateLabs) return renderLoadingCrumb(index, !isLast);

    if (!dateLabs) return null;

    return renderMaybeClickableCrumb(
        intl('labsOn', {
            date: getDateString(date, 'date'),
        }),
        () =>
            profileTabNavigation.navigate('date-labs', {
                profileId,
                date,
            }),
        !isLast,
        index,
        isLast,
    );
};

const LabHistoryCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, labCode] = useAssertParams(navigationProps, 'profileId', 'labCode');

    const { isLoadingLabHistory, labHistory } = useLabHistory({
        medicalProfileId: profileId,
        code: labCode,
    });

    if (isLoadingLabHistory) return renderLoadingCrumb(index, !isLast);

    if (!labHistory?.labHistory?.length) return null;

    const name = labHistory.labHistory.map((_) => _.labType).filter(Boolean)[0]?.display || '';

    if (!name) return null;

    return renderMaybeClickableCrumb(
        name,
        () =>
            profileTabNavigation.navigate('lab-history', {
                profileId,
                labCode,
            }),
        !isLast,
        index,
        isLast,
    );
};

const EmergencyContactCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId, emergencyContactId] = useAssertParams(navigationProps, 'profileId', 'emergencyContactId');

    const { isLoadingProfile, profile } = useMedicalProfile({
        medicalProfileId: profileId,
    });

    if (isLoadingProfile) return renderLoadingCrumb(index, !isLast);

    const targetContact = profile?.emergencyContactDetails.find((_) => _.contactDetailsId === emergencyContactId);

    if (!targetContact) return null;

    return renderMaybeClickableCrumb(
        getName(targetContact),
        () =>
            profileTabNavigation.navigate('edit-emergency-contact', {
                profileId,
                emergencyContactId,
            }),
        !isLast,
        index,
        isLast,
    );
};

const CollaboratorsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const isMobile = useIsMobileLayout();
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('collaborators'),
        () => {
            if (isMobile) {
                profileTabNavigation.navigate('profile', {
                    profileId,
                    defaultTab: 'manage',
                });
            } else {
                profileTabNavigation.navigate('access', {
                    profileId,
                });
            }
        },
        !isLast,
        index,
        isLast,
    );
};

const CardsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('cards'),
        () =>
            profileTabNavigation.navigate('edit', {
                profileId,
                firstName: '',
            }),
        !isLast,
        index,
        isLast,
    );
};

const PortalsCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({ isLast, index, ...navigationProps }) => {
    const isMobile = useIsMobileLayout();
    const profileTabNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('portals'),
        () => {
            if (isMobile) {
                profileTabNavigation.navigate('connect', {
                    profileId,
                });
            } else {
                profileTabNavigation.navigate('connect', {
                    profileId,
                });
            }
        },
        !isLast,
        index,
        isLast,
    );
};

const ClientRecordNotesCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const clientRecordsNavigation = useProfilesTabNavigation();
    const [clientRecordId] = useAssertParams(navigationProps, 'clientRecordId');

    return renderMaybeClickableCrumb(
        intl('client-record-notes'),
        () => clientRecordsNavigation.navigate('client-record', { clientRecordId, defaultTab: 'notes' }),
        !isLast,
        index,
        isLast,
    );
};

const AddClientRecordNoteCrumb = getStaticTextCrumb('add-client-record-note');

const EditClientRecordNoteCrumb = getStaticTextCrumb('edit-client-record-note');

const AddMessageBreadcrumb = getStaticTextCrumb('add-message');

const EditProfileNoteCrumb = getStaticTextCrumb('edit-profile-note');

const ClientActivityHistory = getStaticTextCrumb('client-activity-history');

const AccessCrumb = getStaticTextCrumb('access');

const ConnectionsCrumb = getStaticTextCrumb('connections');

const StaticProfileNotesCrumb = getStaticTextCrumb('static-profile-notes');

const BulkAddClientRecordsCrumb = getStaticTextCrumb('bulk-add-client-records');

const AddSuggestedFoldersCrumb = getStaticTextCrumb('add-suggested-folders');

const EditClientInvitationCrumb = getStaticTextCrumb('edit-client-invitation');

const ProfileActivityCrumb: React.FC<CrumbBaseProps & NativeStackHeaderProps> = ({
    isLast,
    index,
    ...navigationProps
}) => {
    const profilesNavigation = useProfilesTabNavigation();
    const [profileId] = useAssertParams(navigationProps, 'profileId');

    return renderMaybeClickableCrumb(
        intl('profile-activity'),
        () => profilesNavigation.navigate('profile-activity', { profileId }),
        !isLast,
        index,
        isLast,
    );
};

const MessageProfileCrumb = getStaticTextCrumb('message-profile');

const PreferencesCrump = getStaticTextCrumb('preferences');

const BulkAddClientProfilesGroupsCrumb = getStaticTextCrumb('bulk-add-client-profiles-groups');

const InvoiceCrumb = getStaticTextCrumb('invoice');

const CodeStatusCrumb = getStaticTextCrumb('code-status');

const LegalDecisionMakerCrumb = getStaticTextCrumb('legal-decision-maker');

const MessagesCrumb = getStaticTextCrumb('messages');

const SendNoteCrumb = getStaticTextCrumb('send-note');

const VisitInviteEmailsCrumb = getStaticTextCrumb('visit-invite-emails');

export type BreadCrumbPathPartType =
    | 'profiles-root'
    | 'profile'
    | 'notes-root'
    | 'account-root'
    | 'activity-root'
    | 'resource-category'
    | 'profile-notes'
    | 'user-notes'
    | 'audit-log'
    | 'user-note'
    | 'add-note'
    | 'edit-note'
    | 'transfer-note'
    | 'account-information'
    | 'support'
    | 'about'
    | 'subscription-plans'
    | 'privacy-policy'
    | 'account-deletion-confirm'
    | 'add-profile'
    | 'invitation'
    | 'profile'
    | 'edit-profile'
    | 'contact-details'
    | 'emergency-contacts'
    | 'edit-emergency-contact'
    | 'add-emergency-contact'
    | 'care-team'
    | 'edit-care-team'
    | 'add-care-team'
    | 'care-team-member'
    | 'edit-care-team-member'
    | 'copy-care-team-member'
    | 'add-related-care-team-member'
    | 'invite-collaborator'
    | 'invite-details'
    | 'collaborator'
    | 'add-transfer-request'
    | 'transfer-request'
    | 'transfer-request-response'
    | 'add-card'
    | 'edit-card'
    | 'data'
    | 'data-bulk-update'
    | 'data-details'
    | 'add-data'
    | 'edit-data'
    | 'add-portal'
    | 'add-fasten-portal'
    | 'add-tefca-connection'
    | 'add-profile-note'
    | 'copy-profile-note'
    | 'profile-note'
    | 'transfer-profile-note'
    | 'portal-connection'
    | 'fasten-portal-connection'
    | 'reports'
    | 'add-shared-report'
    | 'shared-report'
    | 'files'
    | 'file'
    | 'chat'
    | 'date-labs'
    | 'lab-history'
    | 'emergency-contact'
    | 'collaborators'
    | 'transfer-requests'
    | 'cards'
    | 'portals'
    | 'edit-profile-note'
    | 'profile-from-note-id'
    | 'profile-from-note-destination'
    | 'profile-from-invitation'
    | 'profile-from-transfer-request'
    | 'profile-notes-without-note-id'
    | 'add-message'
    | 'profile-activity'
    | 'message-profile'
    | 'client-records'
    | 'client-record'
    | 'client-record-notes'
    | 'add-client-record-note'
    | 'edit-client-record-note'
    | 'client-record-note'
    | 'preferences'
    | 'client-activity-history'
    | 'access'
    | 'connections'
    | 'static-profile-notes'
    | 'bulk-add-client-records'
    | 'add-suggested-folders'
    | 'edit-client-invitation'
    | 'bulk-add-client-profiles-groups'
    | 'invoice'
    | 'code-status'
    | 'legal-decision-maker'
    | 'messages'
    | 'send-note'
    | 'visit-invite-emails';

export type CrumbBaseProps = {
    isLast: boolean;
    index: number;
};

export type BreadCrumbsOwnProps = {
    path: BreadCrumbPathPartType[];
};

export type BreadCrumbsProps = BreadCrumbsOwnProps & NativeStackHeaderProps;

const BreadCrumbs: React.FC<BreadCrumbsProps> = ({ path, ...headerProps }) => {
    const isProApp = useIsProApp();
    const getPathPart = (pathPart: BreadCrumbPathPartType, index: number, isLast: boolean) => {
        const key = pathPart + String(index);
        const props = { index, isLast, ...headerProps };
        switch (pathPart) {
            case 'profiles-root':
                return <ProfilesRootCrumb {...props} key={key} />;
            case 'profile':
                return <ProfileCrumb {...props} key={key} />;
            case 'profile-notes':
                return <ProfileNotesCrumb {...props} key={key} />;
            case 'account-root':
                return <AccountRootCrumb {...props} key={key} />;
            case 'activity-root':
                return <ActivityRootCrumb {...props} key={key} />;
            case 'resource-category':
                return <ResourceCategoryCrumb {...props} key={key} />;
            case 'user-notes':
                return <UserNotesCrumb {...props} key={key} />;
            case 'audit-log':
                return <AuditLogCrumb {...props} key={key} />;
            case 'user-note':
                return <UserNoteCrumb {...props} key={key} />;
            case 'add-note':
                return <AddNoteCrumb {...props} key={key} />;
            case 'edit-note':
                return <EditNoteCrumb {...props} key={key} />;
            case 'transfer-note':
                return <TransferNoteCrumb {...props} key={key} />;
            case 'account-information':
                return <AccountInformationCrumb {...props} key={key} />;
            case 'support':
                return <SupportCrumb {...props} key={key} />;
            case 'about':
                return <AboutCrumb {...props} key={key} />;
            case 'subscription-plans':
                return <SubscriptionPlansCrumb {...props} key={key} />;
            case 'privacy-policy':
                return <PrivacyPolicyCrumb {...props} key={key} />;
            case 'account-deletion-confirm':
                return <AccountDeletionConfirmCrumb {...props} key={key} />;
            case 'add-profile':
                return <AddProfileCrumb {...props} key={key} />;
            case 'edit-profile':
                return <EditProfileCrumb {...props} key={key} />;
            case 'invitation':
                return <InvitationCrumb {...props} key={key} />;
            case 'contact-details':
                return <ContactDetailsCrumb {...props} key={key} />;
            case 'emergency-contacts':
                return <EmergencyContactsCrumb {...props} key={key} />;
            case 'edit-emergency-contact':
                return <EditEmergencyContactCrumb {...props} key={key} />;
            case 'add-emergency-contact':
                return <AddEmergencyContactCrumb {...props} key={key} />;
            case 'care-team':
                return <CareTeamCrumb {...props} key={key} />;
            case 'edit-care-team':
                return <EditCareTeamCrumb {...props} key={key} />;
            case 'add-care-team':
                return <AddCareTeamCrumb {...props} key={key} />;
            case 'care-team-member':
                return <CareTeamMemberCrumb {...props} key={key} />;
            case 'edit-care-team-member':
                return <EditCareTeamMemberCrumb {...props} key={key} />;
            case 'copy-care-team-member':
                return <CopyCareTeamMemberCrumb {...props} key={key} />;
            case 'add-related-care-team-member':
                return <AddRelatedCareTeamMemberCrumb {...props} key={key} />;
            case 'invite-collaborator':
                return <InviteCollaboratorCrumb {...props} key={key} />;
            case 'invite-details':
                return <InviteDetailsCrumb {...props} key={key} />;
            case 'collaborator':
                return <CollaboratorCrumb {...props} key={key} />;
            case 'add-transfer-request':
                return <AddTransferRequestCrumb {...props} key={key} />;
            case 'transfer-request':
                return <TransferRequestCrumb {...props} key={key} />;
            case 'transfer-request-response':
                return <TransferRequestResponseCrumb {...props} key={key} />;
            case 'add-card':
                return <AddCardCrumb {...props} key={key} />;
            case 'edit-card':
                return <EditCardCrumb {...props} key={key} />;
            case 'data':
                return <DataCrumb {...props} key={key} />;
            case 'data-bulk-update':
                return <DataBulkUpdateCrumb {...props} key={key} />;
            case 'data-details':
                return <DataDetailsCrumb {...props} key={key} />;
            case 'add-data':
                return <AddDataCrumb {...props} key={key} />;
            case 'edit-data':
                return <EditDataCrumb {...props} key={key} />;
            case 'add-portal':
                return <AddPortalCrumb {...props} key={key} />;
            case 'add-fasten-portal':
                return <AddFastenPortalCrumb {...props} key={key} />;
            case 'add-tefca-connection':
                return <AddTefcaConnectionCrumb {...props} key={key} />;
            case 'add-profile-note':
                return <AddProfileNoteCrumb {...props} key={key} />;
            case 'copy-profile-note':
                return <CopyProfileNoteCrumb {...props} key={key} />;
            case 'profile-note':
                return <ProfileNoteCrumb {...props} key={key} />;
            case 'transfer-profile-note':
                return <TransferProfileNoteCrumb {...props} key={key} />;
            case 'portal-connection':
                return <PortalConnectionCrumb {...props} key={key} />;
            case 'fasten-portal-connection':
                return <FastenPortalConnectionCrumb {...props} key={key} />;
            case 'reports':
                return <ReportsCrumb {...props} key={key} />;
            case 'add-shared-report':
                return <AddSharedReportCrumb {...props} key={key} />;
            case 'shared-report':
                return <SharedReportCrumb {...props} key={key} />;
            case 'files':
                return <FilesCrumb {...props} key={key} />;
            case 'file':
                return <FileCrumb {...props} key={key} />;
            case 'chat':
                return <ChatCrumb {...props} key={key} />;
            case 'date-labs':
                return <DateLabsCrumb {...props} key={key} />;
            case 'lab-history':
                return <LabHistoryCrumb {...props} key={key} />;
            case 'emergency-contact':
                return <EmergencyContactCrumb {...props} key={key} />;
            case 'collaborators':
                return <CollaboratorsCrumb {...props} key={key} />;
            case 'transfer-requests':
                return <TransferRequestCrumb {...props} key={key} />;
            case 'cards':
                return <CardsCrumb {...props} key={key} />;
            case 'portals':
                return <PortalsCrumb {...props} key={key} />;
            case 'edit-profile-note':
                return <EditProfileNoteCrumb {...props} key={key} />;
            case 'profile-from-note-id':
                return <ProfileFromNoteIdCrumb {...props} key={key} />;
            case 'profile-from-note-destination':
                return <ProfileFromNoteDestinationCrumb {...props} key={key} />;
            case 'profile-from-invitation':
                return <ProfileFromInvitationCrumb {...props} key={key} />;
            case 'profile-from-transfer-request':
                return <ProfileFromTransferRequestCrumb {...props} key={key} />;
            case 'profile-notes-without-note-id':
                return <ProfileNotesWithoutNoteIdCrumb {...props} key={key} />;
            case 'add-message':
                return <AddMessageBreadcrumb {...props} key={key} />;
            case 'profile-activity':
                return <ProfileActivityCrumb {...props} key={key} />;
            case 'message-profile':
                return <MessageProfileCrumb {...props} key={key} />;
            case 'client-records':
                return <ClientRecordsCrumb {...props} key={key} />;
            case 'client-record':
                return <ClientRecordCrumb {...props} key={key} />;
            case 'client-record-notes':
                return <ClientRecordNotesCrumb {...props} key={key} />;
            case 'add-client-record-note':
                return <AddClientRecordNoteCrumb {...props} key={key} />;
            case 'edit-client-record-note':
                return <EditClientRecordNoteCrumb {...props} key={key} />;
            case 'client-record-note':
                return <ClientRecordNoteCrumb {...props} key={key} />;
            case 'preferences':
                return <PreferencesCrump {...props} key={key} />;
            case 'client-activity-history':
                return <ClientActivityHistory {...props} key={key} />;
            case 'access':
                return <AccessCrumb {...props} key={key} />;
            case 'connections':
                return <ConnectionsCrumb {...props} key={key} />;
            case 'static-profile-notes':
                return <StaticProfileNotesCrumb {...props} key={key} />;
            case 'bulk-add-client-records':
                return <BulkAddClientRecordsCrumb {...props} key={key} />;
            case 'add-suggested-folders':
                return <AddSuggestedFoldersCrumb {...props} key={key} />;
            case 'edit-client-invitation':
                return <EditClientInvitationCrumb {...props} key={key} />;
            case 'bulk-add-client-profiles-groups':
                return <BulkAddClientProfilesGroupsCrumb {...props} key={key} />;
            case 'invoice':
                return <InvoiceCrumb {...props} key={key} />;
            case 'code-status':
                return <CodeStatusCrumb {...props} key={key} />;
            case 'legal-decision-maker':
                return <LegalDecisionMakerCrumb {...props} key={key} />;
            case 'messages':
                return <MessagesCrumb {...props} key={key} />;
            case 'send-note':
                return <SendNoteCrumb {...props} key={key} />;
            case 'visit-invite-emails':
                return <VisitInviteEmailsCrumb {...props} key={key} />;
            default:
                throw new Error(`Unexpected path part: ${pathPart}`);
        }
    };

    const breadcrumbs =
        path.length > 1 ? (
            <Stack horizontal align={'center'} justify={'flex-start'} space={2}>
                {path.map((part, index) => {
                    return getPathPart(part, index, index === path.length - 1);
                })}
            </Stack>
        ) : null;

    const maybeRenderWithOrganizationButton = () => {
        if (isProApp) {
            return (
                <Stack fullWidth horizontal align={'center'} justify={'space-between'} space={4}>
                    <Stack fullHeight horizontal flex={1} align="flex-start">
                        {breadcrumbs}
                    </Stack>
                    <OrganizationButton />
                </Stack>
            );
        } else {
            return breadcrumbs;
        }
    };

    return <BaseHeader height={62} {...headerProps} desktopContent={maybeRenderWithOrganizationButton()} />;
};

export default React.memo(BreadCrumbs);
